// Admin Panel -> ⚙ System -> 📱 SMS Gateway -> 🔔 Notifications.
//
// One place to see every message this app sends BY ITSELF — booking
// confirmations, reminders, receipts, admission, lab and document notices —
// which template each one uses on SMS and on WhatsApp, and whether it is on.
//
// Nothing new is stored. The rows come from code (GET .../events: what is
// actually wired), the on/off state is each channel's template `active` flag —
// the very flag the Templates tabs edit — saved through the existing
// PATCH .../templates/:id, and "would it really go out" is each template's own
// blockers plus its channel's readiness verdict. A switch that is ON can still
// show "not sending yet": switching a draft on does not register it with DLT
// or Meta, and the row says what is missing rather than pretending otherwise.
import { useMemo, useState } from 'react';
import { Bell } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { ApiError } from '@/lib/api/client';
import { toast } from '@/hooks/useToast';
import { useAutomaticEvents, useGateway, useTemplates, useSaveTemplate } from './hooks';
import { useWhatsAppGateway, useWhatsAppTemplates, useSaveWhatsAppTemplate } from '../whatsapp-gateway/hooks';
import type { AutomaticEvent, GatewayIssue, SmsTemplate } from './types';
import type { WhatsAppTemplate } from '../whatsapp-gateway/types';

type Channel = 'sms' | 'whatsapp';

/** One channel's template for one event, reduced to what this screen shows. */
interface CellModel {
  id: number;
  active: boolean;
  ready: boolean;
  blockers: GatewayIssue[];
  /** Which template is in use, in one line. */
  label: string;
  /** False when switching ON is refused by the server (WhatsApp before approval). */
  canEnable: boolean;
  enableHint?: string;
}

function smsCell(t: SmsTemplate): CellModel {
  return {
    id: t.id,
    active: t.active,
    ready: t.ready,
    blockers: t.blockers,
    label: `${t.name} · ${t.dlt_template_id ? `DLT ${t.dlt_template_id}` : 'no DLT ID yet'}`,
    canEnable: true,
  };
}

function whatsAppCell(t: WhatsAppTemplate): CellModel {
  const approved = t.whatsapp_status === 'approved';
  return {
    id: t.id,
    active: t.active,
    ready: t.ready,
    blockers: t.blockers,
    label: `${t.whatsapp_template_name ?? 'no Meta template name'}${t.whatsapp_language_code ? ` (${t.whatsapp_language_code})` : ''} · ${t.whatsapp_status}`,
    canEnable: approved,
    enableHint: approved ? undefined : 'Meta must approve this template first — set its status to approved under WhatsApp -> Templates.',
  };
}

/** The badge and the one-line reason for a cell. */
function stateOf(cell: CellModel, channelLive: boolean): { label: string; variant: 'success' | 'info' | 'warn' | 'neutral'; detail?: string } {
  if (!cell.active) return { label: 'Off', variant: 'neutral' };
  if (!cell.ready) {
    const reason = cell.blockers.find((b) => b.code !== 'template_inactive');
    return { label: 'On — not sending yet', variant: 'warn', detail: reason?.message };
  }
  if (!channelLive) {
    return { label: 'Ready', variant: 'info', detail: 'The template is ready, but this channel is switched off or simulating — see Connection.' };
  }
  return { label: 'Sending', variant: 'success' };
}

function ChannelCell({
  supported, cell, channelLive, busy, onToggle,
}: {
  supported: boolean;
  cell: CellModel | null;
  channelLive: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
}) {
  if (!supported) return <span className="text-[12px] text-ink-muted">Not sent on this channel</span>;
  if (!cell) {
    return (
      <div>
        <Badge variant="neutral">No template</Badge>
        <div className="mt-1 text-[11px] text-ink-muted">Add one under Templates with this event key.</div>
      </div>
    );
  }
  const state = stateOf(cell, channelLive);
  const blockEnable = !cell.active && !cell.canEnable;
  return (
    <div>
      <div className="flex items-center gap-2">
        <Switch
          checked={cell.active}
          disabled={busy || blockEnable}
          onCheckedChange={onToggle}
          aria-label={cell.active ? 'Switch off' : 'Switch on'}
          title={blockEnable ? cell.enableHint : undefined}
        />
        <Badge variant={state.variant}>{state.label}</Badge>
      </div>
      <div className="mt-1 font-mono text-[11px] text-ink-muted">{cell.label}</div>
      {(blockEnable ? cell.enableHint : state.detail) && (
        <div className="mt-0.5 max-w-xs text-[11px] text-ink-muted">{blockEnable ? cell.enableHint : state.detail}</div>
      )}
    </div>
  );
}

export default function NotificationsTab({ canSms = true, canWhatsApp = true }: { canSms?: boolean; canWhatsApp?: boolean }) {
  const events = useAutomaticEvents({ via: canSms ? 'sms' : 'whatsapp' });
  const smsTemplates = useTemplates({ enabled: canSms });
  const waTemplates = useWhatsAppTemplates({ enabled: canWhatsApp });
  const smsGateway = useGateway({ enabled: canSms });
  const waGateway = useWhatsAppGateway({ enabled: canWhatsApp });
  const saveSms = useSaveTemplate();
  const saveWa = useSaveWhatsAppTemplate();

  // A switch-off that needs confirming (the event carries a warning).
  const [confirm, setConfirm] = useState<{ event: AutomaticEvent; channel: Channel; cell: CellModel } | null>(null);

  const smsByKey = useMemo(() => new Map((smsTemplates.data ?? []).map((t) => [t.template_key, smsCell(t)])), [smsTemplates.data]);
  const waByKey = useMemo(() => new Map((waTemplates.data ?? []).map((t) => [t.template_key, whatsAppCell(t)])), [waTemplates.data]);

  // Groups in the order the server returns them.
  const groups = useMemo(() => {
    const out: { group: string; events: AutomaticEvent[] }[] = [];
    for (const e of events.data ?? []) {
      const last = out[out.length - 1];
      if (last && last.group === e.group) last.events.push(e);
      else out.push({ group: e.group, events: [e] });
    }
    return out;
  }, [events.data]);

  const smsLive = !!smsGateway.data?.status.can_send && !smsGateway.data?.status.simulate_only;
  const waLive = !!waGateway.data?.status.can_send && !waGateway.data?.status.simulate_only;

  const busyId = (channel: Channel) => {
    const m = channel === 'sms' ? saveSms : saveWa;
    return m.isPending ? m.variables?.id : undefined;
  };

  const apply = (event: AutomaticEvent, channel: Channel, cell: CellModel, next: boolean) => {
    const done = {
      onSuccess: () => toast({
        title: `${event.name} — ${channel === 'sms' ? 'SMS' : 'WhatsApp'} switched ${next ? 'on' : 'off'}`,
        description: next
          ? 'It is sent automatically from now on, once its template and the channel are ready.'
          : 'The app will not send it on this channel until it is switched back on. Each attempt is still logged.',
      }),
      onError: (e: unknown) => toast({
        variant: 'danger',
        title: 'Could not change that',
        description: e instanceof ApiError ? e.message : 'Please try again.',
      }),
    };
    if (channel === 'sms') saveSms.mutate({ id: cell.id, data: { active: next } }, done);
    else saveWa.mutate({ id: cell.id, data: { active: next } }, done);
  };

  const toggle = (event: AutomaticEvent, channel: Channel, cell: CellModel, next: boolean) => {
    if (!next && event.warning) { setConfirm({ event, channel, cell }); return; }
    apply(event, channel, cell, next);
  };

  if (events.isError) {
    return (
      <ErrorState
        message={events.error instanceof ApiError ? events.error.message : 'Could not load the notification list'}
        onRetry={() => events.refetch()}
      />
    );
  }
  if (events.isLoading || (canSms && smsTemplates.isLoading) || (canWhatsApp && waTemplates.isLoading)) {
    return <LoadingState label="Loading notifications…" />;
  }

  return (
    <div>
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        Every message this hospital sends to patients <strong>automatically</strong>, the template it uses on each
        channel, and whether it is switched on. A switch that is on still needs its template registered (DLT for SMS,
        Meta approval for WhatsApp) and the channel live before anything is delivered — the badge says which step is
        missing. WhatsApp also follows the “Receive messages on WhatsApp” consent form: only patients who accepted it
        while the form is enabled, everyone except patients who declined when it is disabled.
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        {canSms && (
          <div className="rounded-lg border border-border bg-card px-3 py-2 text-[12px]">
            <span className="font-semibold">📱 SMS:</span>{' '}
            <span className="text-ink-muted">{smsGateway.data?.status.summary ?? 'Checking…'}</span>
          </div>
        )}
        {canWhatsApp && (
          <div className="rounded-lg border border-border bg-card px-3 py-2 text-[12px]">
            <span className="font-semibold">💬 WhatsApp:</span>{' '}
            <span className="text-ink-muted">{waGateway.data?.status.summary ?? 'Checking…'}</span>
          </div>
        )}
      </div>

      {groups.map(({ group, events: rows }) => (
        <Card key={group} className="mb-4">
          <CardHeader>
            <CardTitle><Bell size={15} /> {group}</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Message</TableHead>
                  {canSms && <TableHead>📱 SMS</TableHead>}
                  {canWhatsApp && <TableHead>💬 WhatsApp</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e) => {
                  const sms = smsByKey.get(e.template_key) ?? null;
                  const wa = waByKey.get(e.template_key) ?? null;
                  return (
                    <TableRow key={e.template_key}>
                      <TableCell className="max-w-sm align-top">
                        <div className="text-[13px] font-semibold">{e.name}</div>
                        <div className="mt-0.5 text-[12px] text-ink-muted">{e.when}</div>
                        <code className="mt-1 block text-[11px] text-ink-muted">{e.template_key}</code>
                        {e.warning && <div className="mt-1 text-[11px] text-warn-ink">⚠ {e.warning}</div>}
                      </TableCell>
                      {canSms && (
                        <TableCell className="align-top">
                          <ChannelCell
                            supported={e.channels.includes('sms')}
                            cell={sms}
                            channelLive={smsLive}
                            busy={!!sms && busyId('sms') === sms.id}
                            onToggle={(next) => sms && toggle(e, 'sms', sms, next)}
                          />
                        </TableCell>
                      )}
                      {canWhatsApp && (
                        <TableCell className="align-top">
                          <ChannelCell
                            supported={e.channels.includes('whatsapp')}
                            cell={wa}
                            channelLive={waLive}
                            busy={!!wa && busyId('whatsapp') === wa.id}
                            onToggle={(next) => wa && toggle(e, 'whatsapp', wa, next)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ))}

      <Dialog open={!!confirm} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch off {confirm?.event.name}?</DialogTitle>
            <DialogDescription>{confirm?.event.warning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)}>Keep it on</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (confirm) apply(confirm.event, confirm.channel, confirm.cell, false);
                setConfirm(null);
              }}
            >
              Switch off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
